import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  ChevronLeft,
  Cpu,
  Globe,
  Lock,
  Settings,
  Bell,
  Brain,
  LogOut,
  AlertCircle,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { MasterTopbar } from '../shell/MasterTopbar';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatRelativeTime } from '../lib/audit-format';

interface MasterSettings {
  id: string;
  aiProvider: string;
  aiModel: string;
  aiFallbackProvider: string | null;
  aiFallbackModel: string | null;
  aiTemperature: string;
  aiMaxTokens: string;
  aiPromptCachingEnabled: string;
  browserProvider: string;
  ocrConfidenceThreshold: string;
  updatedAt: string;
  updatedBy: string | null;
}

function useMasterSettings() {
  return useQuery({
    queryKey: ['master', 'settings'],
    queryFn: () => api.get<MasterSettings | null>('/v1/master/settings'),
    staleTime: 60_000,
  });
}

interface Section {
  id: string;
  icon: LucideIcon;
  label: string;
  desc: string;
  /** Valor curto mostrado à direita na lista. */
  value: (s: MasterSettings | null) => string;
  /** Pares chave/valor pra detalhe. */
  rows: (s: MasterSettings | null) => Array<[string, string]>;
}

const SECTIONS: Section[] = [
  {
    id: 'ia-modelo',
    icon: Cpu,
    label: 'IA · Modelo primário',
    desc: 'Provedor + modelo Anthropic/OpenAI',
    value: (s) => s?.aiModel ?? '—',
    rows: (s) => [
      ['Provedor', s?.aiProvider ?? '—'],
      ['Modelo', s?.aiModel ?? '—'],
      ['Temperatura', s?.aiTemperature ?? '—'],
      ['Max tokens', s?.aiMaxTokens ?? '—'],
    ],
  },
  {
    id: 'ia-fallback',
    icon: Brain,
    label: 'IA · Fallback',
    desc: 'Modelo de contingência',
    value: (s) => s?.aiFallbackModel ?? '—',
    rows: (s) => [
      ['Provedor', s?.aiFallbackProvider ?? '—'],
      ['Modelo', s?.aiFallbackModel ?? '—'],
    ],
  },
  {
    id: 'browser',
    icon: Globe,
    label: 'Browser provider',
    desc: 'Steel/Browserbase pro portal',
    value: (s) => s?.browserProvider ?? '—',
    rows: (s) => [['Provider ativo', s?.browserProvider ?? '—']],
  },
  {
    id: 'ocr',
    icon: Settings,
    label: 'OCR',
    desc: 'Threshold de confiança',
    value: (s) => (s ? `${Math.round(Number(s.ocrConfidenceThreshold) * 100)}%` : '—'),
    rows: (s) => [
      ['Threshold', s ? `${Math.round(Number(s.ocrConfidenceThreshold) * 100)}%` : '—'],
      ['Regra', 'acima → auto-aprovado; abaixo → revisão'],
    ],
  },
  {
    id: 'cache',
    icon: Lock,
    label: 'Prompt caching',
    desc: 'Cache de KB no provedor',
    value: (s) => (s?.aiPromptCachingEnabled === 'true' ? 'ativo' : 'inativo'),
    rows: (s) => [['Status', s?.aiPromptCachingEnabled === 'true' ? 'ativo' : 'inativo']],
  },
  {
    id: 'notif',
    icon: Bell,
    label: 'Notificações',
    desc: 'Email + webhook',
    value: () => 'padrão',
    rows: () => [
      ['Email', 'eventos críticos'],
      ['Webhook', '—'],
    ],
  },
];

export function MasterConfigScreen() {
  const { data, isLoading, error } = useMasterSettings();
  const [sel, setSel] = useState<Section | null>(null);
  const { logout } = useAuth();
  const qc = useQueryClient();

  if (sel) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '0 18px 28px' }}>
        <BackHeader title={sel.label} sub="Configuração global" onBack={() => setSel(null)} />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: 16,
            background: 'var(--card)',
            border: '1px solid var(--card-stroke)',
            borderRadius: 18,
          }}
        >
          <span
            style={{
              width: 46,
              height: 46,
              borderRadius: 13,
              background: 'var(--lilac-soft)',
              color: 'var(--lilac-deep)',
              display: 'grid',
              placeItems: 'center',
              flex: '0 0 46px',
            }}
          >
            <sel.icon size={22} strokeWidth={1.7} />
          </span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg)' }}>{sel.label}</div>
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{sel.desc}</div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--info-soft)',
            color: 'var(--info)',
            borderRadius: 14,
            padding: '12px 14px',
            fontSize: 12.5,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <AlertCircle size={16} style={{ flex: '0 0 16px', marginTop: 1 }} strokeWidth={1.8} />
          <span>
            Configurações globais detalhadas são editadas no desktop. Aqui você acompanha o estado
            atual.
          </span>
        </div>

        <div
          style={{
            padding: '4px 14px',
            background: 'var(--card)',
            border: '1px solid var(--card-stroke)',
            borderRadius: 14,
          }}
        >
          {sel.rows(data ?? null).map(([k, v], i, a) => (
            <div
              key={k}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                padding: '11px 0',
                borderBottom: i < a.length - 1 ? '1px solid var(--card-stroke)' : 0,
              }}
            >
              <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{k}</span>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: 'right',
                  color: 'var(--fg)',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {v}
              </span>
            </div>
          ))}
          {data?.updatedAt && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                padding: '11px 0',
                borderTop: '1px solid var(--card-stroke)',
              }}
            >
              <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Última alteração</span>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--fg)',
                }}
              >
                {formatRelativeTime(data.updatedAt)}
              </span>
            </div>
          )}
        </div>

        <button
          onClick={() => {
            qc.invalidateQueries({ queryKey: ['master', 'settings'] });
            toast.success('Configurações revalidadas.');
          }}
          style={{
            width: '100%',
            padding: '12px 16px',
            background: 'var(--lilac-deep)',
            color: '#fff',
            border: 0,
            borderRadius: 14,
            fontSize: 13.5,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Recarregar do servidor
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '0 18px 28px' }}>
      <MasterTopbar title="Configurações" sub="Plataforma · global" />

      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ height: 56, background: 'var(--card-soft)', borderRadius: 12 }} />
          ))}
        </div>
      )}

      {error && (
        <div
          style={{
            padding: 14,
            background: 'var(--block-soft)',
            color: 'var(--block)',
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          {(error as Error).message}
        </div>
      )}

      {!isLoading && (
        <div
          style={{
            padding: 6,
            background: 'var(--card)',
            border: '1px solid var(--card-stroke)',
            borderRadius: 18,
          }}
        >
          {SECTIONS.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setSel(c)}
              style={{
                textAlign: 'left',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '11px 10px',
                borderBottom: i < SECTIONS.length - 1 ? '1px solid var(--card-stroke)' : 0,
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
                font: 'inherit',
                color: 'var(--fg)',
              }}
            >
              <span
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 11,
                  background: 'var(--card-soft)',
                  color: 'var(--fg-muted)',
                  display: 'grid',
                  placeItems: 'center',
                  flex: '0 0 38px',
                }}
              >
                <c.icon size={17} strokeWidth={1.7} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{c.label}</div>
                <div
                  style={{
                    fontSize: 10.5,
                    color: 'var(--fg-muted)',
                    fontFamily: 'var(--font-mono)',
                    marginTop: 2,
                  }}
                >
                  {c.desc}
                </div>
              </div>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  color: 'var(--lilac-deep)',
                  marginRight: 4,
                  maxWidth: 100,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.value(data ?? null)}
              </span>
              <ChevronRight size={15} style={{ color: 'var(--fg-dim)' }} />
            </button>
          ))}
        </div>
      )}

      <button
        onClick={() => {
          void logout();
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '12px 16px',
          background: 'var(--card-soft)',
          border: '1px solid var(--card-stroke)',
          color: 'var(--fg)',
          borderRadius: 14,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <LogOut size={15} strokeWidth={1.7} /> Sair do modo Master
      </button>
    </div>
  );
}

interface BackHeaderProps {
  title: string;
  sub?: string;
  onBack: () => void;
}
function BackHeader({ title, sub, onBack }: BackHeaderProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0 8px' }}>
      <button
        onClick={onBack}
        aria-label="Voltar"
        style={{
          width: 36,
          height: 36,
          borderRadius: 12,
          background: 'var(--card-soft)',
          border: '1px solid var(--card-stroke)',
          color: 'var(--fg)',
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer',
        }}
      >
        <ChevronLeft size={16} strokeWidth={1.8} />
      </button>
      <div style={{ minWidth: 0 }}>
        {sub && (
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontFamily: 'var(--font-mono)',
              color: 'var(--fg-muted)',
              marginBottom: 2,
            }}
          >
            {sub}
          </div>
        )}
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'var(--fg)',
          }}
        >
          {title}
        </div>
      </div>
    </div>
  );
}
